import React from 'react';
import { Badge, Button, Checkbox, Table, TablePagination, TableSelection } from 'AWS-UI-Components-React';
import Modal from '@amzn/awsui-components-react-v3/polaris/modal';
import SpaceBetween from '@amzn/awsui-components-react-v3/polaris/space-between';
import StatusIndicator from '@amzn/awsui-components-react-v3/polaris/status-indicator';
import Creatable from 'react-select/lib/Creatable';
import Select from 'react-select';
import { CHANNEL_MAPPING } from './channel_mapping_constants';
import { downloadFile } from '../../../utilities';

const ACCEPTED_EXTENSIONS = '.csv,.tsv,.xls';

export class CMBulkUpload extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      file: null,

      reviewItems: [],
      selectedItems: [],
      editValues: {},
      status: '',
      statusMessage: '',
      uploadDisabled: true,
      showReview: false,
    };
  }

  handleFileChange = (event) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const ext = file.name.split('.').pop().toLowerCase();
      if (['csv', 'tsv', 'xls'].includes(ext)) {
        this.setState({ file, uploadDisabled: false, status: '', statusMessage: '', showReview: false });
      } else {
        this.setState({ uploadDisabled: true, status: 'error', statusMessage: `Please select a .csv or .tsv file (or .xls exported from Download Data)` });
      }
    }
  };

  onParseFile = () => {
    const { file } = this.state;
    if (!file) return;

    this.setState({ status: 'loading', statusMessage: 'Parsing file...' });
    this.parseCSV(file);
  };

  parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const delimiter = file.name.endsWith('.tsv') || file.name.endsWith('.xls') ? '\t' : ',';
        const lines = text.split('\n').filter(line => line.trim());
        const rawHeaders = lines[0].split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
        const headers = rawHeaders.map(h => this.normalizeHeader(h));

        const rows = lines.slice(1).map(line => {
          const values = line.split(delimiter).map(v => v.trim().replace(/^"|"$/g, ''));
          const row = {};
          headers.forEach((header, i) => { row[header] = values[i] || ''; });
          return row;
        });
        this.processRows(rows);
      } catch (error) {
        this.setState({ status: 'error', statusMessage: `Failed to parse file: ${error.message}` });
      }
    };
    reader.readAsText(file);
  };

  normalizeHeader = (header) => {
    const mapping = {
      'Cognos Channel Type': 'cognosChannelType',
      'Cognos Channel Description': 'channelDescription',
      'Cognos Parent Channel': 'cognosParentChannel',
      'Cognos Channel': 'cognosChannel',
      'Derived Cognos Channel': 'derivedCognosChannel',
      'Cognos Group Code': 'groupCode',
      'HCP Merchant Channel': 'hcpMerchantChannel',
      'HCP Channel Type': 'hcpChannelType',
      'Marketplace Id': 'marketplaceId',
      'Derived Marketplace Id': 'derivedMarketplaceId',
      'Marketplace Name': 'marketplaceName',
      'Cognos Excluded': 'excluded',
      'HCP Excluded': 'hcpExcluded',
      'Ignored': 'ignored',
      'Comments': 'comments',
    };
    return mapping[header] || header;
  };

  getValidationMaps = () => {
    const { dropdownOptions } = this.props;
    return {
      selectOnly: {
        hcpMerchantChannel: (dropdownOptions.hcpChannelOptions || []).map(o => o.value),
        derivedCognosChannel: (dropdownOptions.hcpChannelOptions || []).map(o => o.value),
        marketplaceId: (dropdownOptions.marketplaceIdOptions || []).map(o => String(o.value)),
        cognosParentChannel: (dropdownOptions.cognosParentChannelOptions || []).map(o => o.value),
        cognosChannel: (dropdownOptions.cognosChannelOptions || []).map(o => o.value),
        groupCode: (dropdownOptions.groupCodeOptions || []).map(o => o.value),
      },
      creatable: {
        hcpChannelType: (dropdownOptions.hcpChannelTypeOptions || []).map(o => o.value),
      },
    };
  };

  processRows = (rows) => {
    const { channelMappingsById } = this.props;
    const reviewItems = [];

    for (const row of rows) {
      const cognosChannelType = String(row.cognosChannelType || '').trim();
      const hcpChannelType = String(row.hcpChannelType || '').trim();

      if (!cognosChannelType || !hcpChannelType) continue;

      const id = `${cognosChannelType}#${hcpChannelType}#*`;
      const existing = channelMappingsById[id];

      const mapping = {
        id,
        cognosChannelType,
        channelDescription: row.channelDescription || '',
        cognosParentChannel: row.cognosParentChannel || '',
        cognosChannel: row.cognosChannel || '',
        derivedCognosChannel: row.derivedCognosChannel || '',
        groupCode: row.groupCode || '',
        hcpMerchantChannel: (row.hcpMerchantChannel || '').toUpperCase(),
        hcpChannelType,
        phoenixChannel: (row.hcpMerchantChannel || '').toUpperCase(),
        marketplaceId: row.marketplaceId ? parseInt(row.marketplaceId) : '',
        derivedMarketplaceId: row.derivedMarketplaceId ? parseInt(row.derivedMarketplaceId) : '',
        marketplaceName: row.marketplaceName || '',
        excluded: this.parseBool(row.excluded),
        hcpExcluded: this.parseBool(row.hcpExcluded),
        ignored: this.parseBool(row.ignored),
        comments: row.comments || '',
      };

      if (!existing) {
        reviewItems.push({ ...mapping, _badge: 'NEW' });
      } else if (mapping.excluded && mapping.ignored && !(existing.excluded && existing.ignored)) {
        reviewItems.push({ ...mapping, _badge: 'DELETED', _original: existing });
      } else if (this.hasChanges(existing, mapping)) {
        reviewItems.push({ ...mapping, _badge: 'EDITED', _original: existing });
      } else {
        reviewItems.push({ ...mapping, _badge: 'NO_CHANGES' });
      }
    }

    const newCount = reviewItems.filter(r => r._badge === 'NEW').length;
    const editedCount = reviewItems.filter(r => r._badge === 'EDITED').length;
    const deletedCount = reviewItems.filter(r => r._badge === 'DELETED').length;
    const noChangeCount = reviewItems.filter(r => r._badge === 'NO_CHANGES').length;

    // Validate select-only fields + badge new creatable values
    const { dropdownOptions } = this.props;
    const { selectOnly: selectOnlyValidation, creatable: creatableValidation } = this.getValidationMaps();

    let errorCount = 0;
    for (const item of reviewItems) {
      item._errors = [];
      item._newFields = [];
      if (item._badge === 'NO_CHANGES') continue;

      // Check select-only fields
      for (const [field, validValues] of Object.entries(selectOnlyValidation)) {
        const val = item[field] != null ? String(item[field]) : '';
        if (val && !validValues.includes(val)) {
          item._errors.push(field);
          errorCount++;
        }
      }
      // Validate derivedMarketplaceId against its parent marketplaceId
      if (item.derivedMarketplaceId) {
        const config = (this.props.marketplaceConfig || []).find(c =>
          c.derivedMarketplaceIds && c.derivedMarketplaceIds.some(id => Number(id) === Number(item.marketplaceId))
        );
        const validDerived = config ? config.derivedMarketplaceIds.map(String) : [];
        if (!validDerived.includes(String(item.derivedMarketplaceId))) {
          item._errors.push('derivedMarketplaceId');
          errorCount++;
        }
      }
      // Badge new creatable values
      for (const [field, existingValues] of Object.entries(creatableValidation)) {
        const val = item[field] != null ? String(item[field]) : '';
        if (val && !existingValues.includes(val)) {
          item._newFields.push(field);
        }
      }
      if (item._errors.length > 0) {
        item._originalBadge = item._badge;
        item._badge = 'ERROR';
      }
    }

    this.setState({
      reviewItems,
      selectedItems: reviewItems.filter(r => r._badge !== 'NO_CHANGES' && r._badge !== 'ERROR'),
      showReview: true,
      file: null,
      uploadDisabled: true,
      status: errorCount > 0 ? 'warning' : 'success',
      statusMessage: `Parsed ${reviewItems.length} rows: ${newCount} new, ${editedCount} edited, ${deletedCount} deleted, ${noChangeCount} no changes${errorCount > 0 ? `, ${errorCount} errors (invalid field values)` : ''}`,
    });
    if (this._fileInput) this._fileInput.value = '';
  };

  parseBool = (value) => {
    if (typeof value === 'boolean') return value;
    const str = String(value).toLowerCase().trim();
    return str === 'true' || str === 'yes' || str === '1';
  };

  hasChanges = (existing, incoming) => {
    const fieldsToCheck = ['hcpMerchantChannel', 'channelDescription', 'cognosParentChannel',
      'cognosChannel', 'derivedCognosChannel', 'groupCode', 'marketplaceId', 'derivedMarketplaceId',
      'marketplaceName', 'excluded', 'hcpExcluded', 'ignored'];
    return fieldsToCheck.some(field => {
      const existVal = existing[field] == null ? '' : String(existing[field]);
      const newVal = incoming[field] == null ? '' : String(incoming[field]);
      return existVal !== newVal;
    });
  };

  onConfirmUpload = () => {
    const { selectedItems, reviewItems } = this.state;
    const selectedIds = new Set(selectedItems.map(s => s.id));
    const itemsToProcess = reviewItems.filter(r => selectedIds.has(r.id) && r._badge !== 'NO_CHANGES' && r._badge !== 'ERROR');

    if (itemsToProcess.length === 0) {
      this.setState({ status: 'info', statusMessage: 'No changes to apply.' });
      return;
    }

    const newItems = itemsToProcess.filter(r => r._badge === 'NEW');
    const editedItems = itemsToProcess.filter(r => r._badge === 'EDITED');
    const deletedItems = itemsToProcess.filter(r => r._badge === 'DELETED');

    // Add new mappings
    const newMappingsToAdd = [];
    for (const item of newItems) {
      const { _badge, _original, _errors, _newFields, _originalBadge, ...mapping } = item;
      mapping.version = '0';
      mapping.comments = mapping.comments || `Bulk upload by ${this.props.user}`;
      newMappingsToAdd.push(mapping);
    }
    if (newMappingsToAdd.length > 0) {
      this.props.addChannelMappings(newMappingsToAdd);
    }

    // Stage edited + deleted mappings together
    const modifiedById = {};
    for (const item of editedItems) {
      const { _badge, _original, _errors, _newFields, _originalBadge, ...mapping } = item;
      mapping.comments = mapping.comments || `Bulk edit by ${this.props.user}`;
      modifiedById[mapping.id] = mapping;
    }
    for (const item of deletedItems) {
      const { _badge, _original, _errors, _newFields, _originalBadge, ...mapping } = item;
      mapping.comments = `Bulk delete by ${this.props.user}`;
      modifiedById[mapping.id] = mapping;
    }
    if (Object.keys(modifiedById).length > 0) {
      this.props.stageChannelMappingChanges(modifiedById);
    }

    this.setState({ file: null, reviewItems: [], selectedItems: [], showReview: false, uploadDisabled: true, status: 'success', statusMessage: `Applied ${itemsToProcess.length} changes (${newItems.length} new, ${editedItems.length} edited, ${deletedItems.length} deleted)` });
    if (this._fileInput) this._fileInput.value = '';
  };

  onAddEntry = () => {
    const newEntry = {
      id: `new_${Date.now()}#new#*`,
      cognosChannelType: '',
      channelDescription: '',
      cognosParentChannel: '',
      cognosChannel: '',
      derivedCognosChannel: '',
      groupCode: '',
      hcpMerchantChannel: '',
      hcpChannelType: '',
      phoenixChannel: '',
      marketplaceId: '',
      derivedMarketplaceId: '',
      marketplaceName: '',
      excluded: false,
      hcpExcluded: false,
      ignored: false,
      comments: '',
      _badge: 'NEW',
      _errors: [],
      _newFields: [],
    };
    const { reviewItems, selectedItems } = this.state;
    this.setState({
      reviewItems: [newEntry, ...reviewItems],
      selectedItems: [newEntry, ...selectedItems],
      showReview: true,
    });
  };

  onDownloadTemplate = () => {
    const headerNames = [
      'Cognos Channel Type', 'Cognos Channel Description', 'Cognos Parent Channel', 'Cognos Channel',
      'Derived Cognos Channel', 'Cognos Group Code', 'HCP Merchant Channel', 'HCP Channel Type', 'Marketplace Id',
      'Derived Marketplace Id', 'Marketplace Name', 'Cognos Excluded', 'HCP Excluded', 'Ignored', 'Comments'
    ];
    const headers = headerNames.join('\t');
    const exampleRow = headerNames.map(col => {
      switch(col) {
        case 'Cognos Channel Type': return '101';
        case 'HCP Channel Type': return '1';
        case 'Cognos Excluded': return 'false';
        case 'HCP Excluded': return 'false';
        case 'Ignored': return 'false';
        case 'Marketplace Id': return '1';
        case 'Derived Marketplace Id': return '1';
        default: return '';
      }
    }).join('\t');
    const data = `${headers}\n${exampleRow}`;
    downloadFile(data, 'Channel_Mapping_Template.xls');
  };

  getBadge = (item) => {
    const badgeConfig = CHANNEL_MAPPING.BADGE_TYPES[item._badge];
    if (!badgeConfig) return null;
    return <Badge color={badgeConfig.color}>{badgeConfig.label}</Badge>;
  };

  onReviewSelectionChange = (event) => {
    this.setState({ selectedItems: event.detail.selectedItems });
  };

  onReviewFieldBlur = (item, field, newValue) => {
    const { reviewItems } = this.state;
    const { dropdownOptions, marketplaceConfig } = this.props;

    const { selectOnly: selectOnlyFields, creatable: creatableFieldsMap } = this.getValidationMaps();

    const updated = reviewItems.map(r => {
      if (r.id !== item.id) return r;
      let updatedRow = { ...r, [field]: newValue };

      // Recalculate ID when key fields change
      if (field === 'cognosChannelType' || field === 'hcpChannelType') {
        updatedRow.id = `${updatedRow.cognosChannelType}#${updatedRow.hcpChannelType}#*`;
      }

      // Auto-resolve marketplace fields
      if (field === 'marketplaceId') {
        const mpVal = Number(newValue);
        const config = (marketplaceConfig || []).find(c =>
          c.derivedMarketplaceIds && c.derivedMarketplaceIds.some(id => Number(id) === mpVal)
        );
        updatedRow.marketplaceName = config ? config.detail : '';
        const derivedIds = config ? config.derivedMarketplaceIds : [];
        if (!derivedIds.some(id => Number(id) === Number(updatedRow.derivedMarketplaceId))) {
          updatedRow.derivedMarketplaceId = newValue;
        }
      }

      // Re-validate errors
      updatedRow._errors = [];
      for (const [f, validValues] of Object.entries(selectOnlyFields)) {
        const val = updatedRow[f] != null ? String(updatedRow[f]) : '';
        if (val && !validValues.includes(val)) {
          updatedRow._errors.push(f);
        }
      }
      // Validate derivedMarketplaceId
      if (updatedRow.derivedMarketplaceId) {
        const mpConfig = (marketplaceConfig || []).find(c =>
          c.derivedMarketplaceIds && c.derivedMarketplaceIds.some(id => Number(id) === Number(updatedRow.marketplaceId))
        );
        const validDerived = mpConfig ? mpConfig.derivedMarketplaceIds.map(String) : [];
        if (!validDerived.includes(String(updatedRow.derivedMarketplaceId))) {
          updatedRow._errors.push('derivedMarketplaceId');
        }
      }
      updatedRow._badge = updatedRow._errors.length > 0 ? 'ERROR' : (r._badge === 'ERROR' ? (r._originalBadge || 'NEW') : r._badge);
      if (updatedRow._badge === 'NO_CHANGES' && r._original) {
        updatedRow._badge = 'EDITED';
      }
      // Recalculate _newFields for creatable fields
      updatedRow._newFields = [];
      for (const [f, existingValues] of Object.entries(creatableFieldsMap)) {
        const val = updatedRow[f] != null ? String(updatedRow[f]) : '';
        if (val && !existingValues.includes(val)) {
          updatedRow._newFields.push(f);
        }
      }
      return updatedRow;
    });
    // Rebuild selectedItems from updated reviewItems (handles ID changes)
    const { selectedItems } = this.state;
    const oldSelectedIds = new Set(selectedItems.map(s => s.id));
    // If the edited item's ID changed, include the new ID
    const editedItem = updated.find(r => r.cognosChannelType === (field === 'cognosChannelType' ? newValue : item.cognosChannelType) && r.hcpChannelType === (field === 'hcpChannelType' ? newValue : item.hcpChannelType));
    let newSelected = updated.filter(r => oldSelectedIds.has(r.id) || (editedItem && r.id === editedItem.id && oldSelectedIds.has(item.id)));
    // Auto-select newly EDITED items
    if (editedItem && editedItem._badge === 'EDITED' && !oldSelectedIds.has(item.id)) {
      newSelected.push(editedItem);
    }
    this.setState({ reviewItems: updated, selectedItems: newSelected });
  };

  onRemoveReviewItem = (item) => {
    const { reviewItems, selectedItems } = this.state;
    const remaining = reviewItems.filter(r => r.id !== item.id);
    if (remaining.length === 0) {
      this.onDismiss();
      return;
    }
    this.setState({
      reviewItems: remaining,
      selectedItems: selectedItems.filter(r => r.id !== item.id),
    });
  };

  renderBoolCell = (item, field) => {
    if (item._badge === 'DELETED') return <Checkbox checked={Boolean(item[field])} disabled />;
    const hasChanged = item._original && Boolean(item._original[field]) !== Boolean(item[field]);
    return (
      <div>
        <Checkbox
          checked={Boolean(item[field])}
          onChange={() => {
            this.onReviewFieldBlur(item, field, !item[field]);
          }}
        />
        {hasChanged && <del style={{ color: '#888', fontSize: '0.85em' }}>{Boolean(item._original[field]) ? 'Yes' : 'No'}</del>}
      </div>
    );
  };

  getReviewColumnDefinitions = () => {
    const { dropdownOptions } = this.props;
    const creatableFields = {
      hcpChannelType: dropdownOptions.hcpChannelTypeOptions,
    };
    const selectFields = {
      hcpMerchantChannel: dropdownOptions.hcpChannelOptions,
      derivedCognosChannel: dropdownOptions.hcpChannelOptions,
      cognosParentChannel: dropdownOptions.cognosParentChannelOptions,
      cognosChannel: dropdownOptions.cognosChannelOptions,
      groupCode: dropdownOptions.groupCodeOptions,
      marketplaceId: dropdownOptions.marketplaceIdOptions,
    };

    const readOnlyFields = ['marketplaceName'];

    const diffCell = (item, field) => {
      if (item._badge === 'DELETED') {
        return <del style={{ color: '#d13212' }}>{item[field]}</del>;
      }

      // Read-only fields (auto-set from marketplaceId)
      if (readOnlyFields.includes(field)) {
        return <span>{item[field] || ''}</span>;
      }

      // derivedMarketplaceId: dynamic dropdown filtered by current marketplaceId
      if (field === 'derivedMarketplaceId') {
        const config = (this.props.marketplaceConfig || []).find(c =>
          c.derivedMarketplaceIds && c.derivedMarketplaceIds.some(id => Number(id) === Number(item.marketplaceId))
        );
        const derivedIds = config ? config.derivedMarketplaceIds : [];
        const options = derivedIds.map(id => ({ value: String(id), label: `${id}` }));
        const val = item.derivedMarketplaceId != null ? String(item.derivedMarketplaceId) : '';
        const currentValue = val ? options.find(o => o.value === val) || { value: val, label: val } : null;
        const isError = item._errors && item._errors.includes(field);
        return (
          <div>
            <Select
              options={options}
              value={currentValue}
              onChange={(selected) => {
                if (selected) this.onReviewFieldBlur(item, field, selected.value);
              }}
              isClearable={false}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                container: base => ({ ...base, minWidth: 100 }),
                control: base => isError ? { ...base, borderColor: '#d13212', boxShadow: '0 0 0 1px #d13212' } : base,
              }}
            />
            {isError && <span style={{ color: '#d13212', fontSize: '0.8em' }}>Invalid value</span>}
          </div>
        );
      }

      const oldVal = item._original ? String(item._original[field] == null ? '' : item._original[field]) : null;
      const newVal = item[field] != null ? String(item[field]) : '';
      const hasChanged = oldVal !== null && oldVal !== newVal;

      // Dropdown fields (Creatable or Select)
      const dropdownOpts = creatableFields[field] || selectFields[field];
      if (dropdownOpts) {
        const options = dropdownOpts || [];
        const currentValue = newVal ? { value: newVal, label: newVal } : null;
        const Component = creatableFields[field] ? Creatable : Select;
        const isError = item._errors && item._errors.includes(field);
        const isNewValue = item._newFields && item._newFields.includes(field);
        return (
          <div>
            <Component
              options={options}
              value={currentValue}
              onChange={(selected) => {
                if (selected) this.onReviewFieldBlur(item, field, selected.value);
              }}
              isClearable={false}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                container: base => ({ ...base, minWidth: 120 }),
                control: base => isError ? { ...base, borderColor: '#d13212', boxShadow: '0 0 0 1px #d13212' } : base,
              }}
            />
            {isError && <span style={{ color: '#d13212', fontSize: '0.8em' }}>Invalid value</span>}
            {isNewValue && <Badge color="green">New</Badge>}
            {hasChanged && <del style={{ color: '#888', fontSize: '0.85em' }}>{oldVal}</del>}
          </div>
        );
      }

      // Plain text input for other fields
      const editKey = `${item.id}::${field}`;
      const isEditing = this.state.editValues && editKey in this.state.editValues;
      const displayValue = isEditing ? this.state.editValues[editKey] : newVal;

      return (
        <div>
          <input
            type="text"
            value={displayValue}
            onChange={(e) => {
              const val = e.target.value;
              this.setState(prev => ({
                editValues: { ...prev.editValues, [editKey]: val }
              }));
            }}
            onFocus={() => {
              if (!isEditing) {
                this.setState(prev => ({
                  editValues: { ...prev.editValues, [editKey]: newVal }
                }));
              }
            }}
            onBlur={() => {
              if (!isEditing) return;
              const val = this.state.editValues[editKey];
              this.setState(prev => {
                const { [editKey]: _, ...rest } = prev.editValues;
                return { editValues: rest };
              });
              if (String(val).trim() !== newVal.trim()) {
                this.onReviewFieldBlur(item, field, val.trim());
              }
            }}
            style={{ width: '100%', padding: '2px 4px', border: '1px solid #d5dbdb', borderRadius: '2px' }}
          />
          {hasChanged && <del style={{ color: '#888', fontSize: '0.85em' }}>{oldVal}</del>}
        </div>
      );
    };

    return [
      { id: 'status', header: 'Status', cell: (item) => this.getBadge(item), minWidth: '80px' },
      { id: 'cognosChannelType', header: 'Cognos Channel Type', cell: (item) => {
        const isNewCognosType = !Object.keys(this.props.channelMappingsById || {}).some(id => id.startsWith(item.cognosChannelType + '#'));
        return <div>{diffCell(item, 'cognosChannelType')} {isNewCognosType && <Badge color="green">New</Badge>}</div>;
      }},
      { id: 'channelDescription', header: 'Channel Description', cell: (item) => diffCell(item, 'channelDescription') },
      { id: 'cognosParentChannel', header: 'Cognos Parent Channel', cell: (item) => diffCell(item, 'cognosParentChannel') },
      { id: 'cognosChannel', header: 'Cognos Channel', cell: (item) => diffCell(item, 'cognosChannel') },
      { id: 'derivedCognosChannel', header: 'Derived Cognos Channel', cell: (item) => diffCell(item, 'derivedCognosChannel') },
      { id: 'groupCode', header: 'Group Code', cell: (item) => diffCell(item, 'groupCode') },
      { id: 'hcpMerchantChannel', header: 'HCP Merchant Channel', cell: (item) => diffCell(item, 'hcpMerchantChannel') },
      { id: 'hcpChannelType', header: 'HCP Channel Type', cell: (item) => diffCell(item, 'hcpChannelType') },
      { id: 'marketplaceId', header: 'Marketplace Id', cell: (item) => diffCell(item, 'marketplaceId') },
      { id: 'derivedMarketplaceId', header: 'Derived Marketplace Id', cell: (item) => diffCell(item, 'derivedMarketplaceId') },
      { id: 'marketplaceName', header: 'Marketplace Name', cell: (item) => diffCell(item, 'marketplaceName') },
      { id: 'excluded', header: 'Cognos Excluded', cell: (item) => this.renderBoolCell(item, 'excluded') },
      { id: 'hcpExcluded', header: 'HCP Excluded', cell: (item) => this.renderBoolCell(item, 'hcpExcluded') },
      { id: 'comments', header: 'Comments', cell: (item) => diffCell(item, 'comments') },
      { id: 'actions', header: 'Actions', minWidth: '150px', cell: (item) => (
        <Button variant="normal" onClick={() => this.onRemoveReviewItem(item)}>Remove</Button>
      )},
    ];
  };

  onDismiss = () => {
    this.setState({ file: null, reviewItems: [], status: '', statusMessage: '', uploadDisabled: true, showReview: false });
    this.props.onDismiss();
  };

  render() {
    const { visible } = this.props;
    const { status, statusMessage, showReview, reviewItems, uploadDisabled } = this.state;

    const hasActionableItems = reviewItems.some(r => r._badge !== 'NO_CHANGES');

    return (
      <Modal
        visible={visible}
        header="Bulk Upload Channel Mappings"
        onDismiss={this.onDismiss}
        size="max"
        footer={
          <span className="awsui-util-f-r">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="normal" onClick={this.onDownloadTemplate} icon="download">
                Download Template
              </Button>
              <Button variant="normal" onClick={this.onAddEntry} icon="add-plus">
                Add Entry
              </Button>
              {showReview && (
                <Button variant="primary" onClick={this.onConfirmUpload}
                  disabled={!this.state.selectedItems.length || this.state.selectedItems.every(r => r._badge === 'NO_CHANGES')}>
                  Stage Selected ({this.state.selectedItems.filter(r => r._badge !== 'NO_CHANGES').length})
                </Button>
              )}
            </SpaceBetween>
          </span>
        }
      >
        <SpaceBetween size="m">
          <div>
            <input
              type="file"
              accept={ACCEPTED_EXTENSIONS}
              onChange={this.handleFileChange}
              ref={(el) => { this._fileInput = el; }}
              multiple={false}
            />
            <Button variant="primary" onClick={this.onParseFile} disabled={uploadDisabled} icon="upload">
              Parse File
            </Button>
          </div>

          {status && (
            <StatusIndicator type={status}>
              {statusMessage}
            </StatusIndicator>
          )}

          {showReview && (
            <Table
              columnDefinitions={this.getReviewColumnDefinitions()}
              items={reviewItems}
              header={
                <h3>Upload Review ({reviewItems.length} rows, {this.state.selectedItems.length} selected)</h3>
              }
              empty="No data parsed."
              stickyHeader
            >
              <TableSelection
                onSelectionChange={this.onReviewSelectionChange}
                selectedItems={this.state.selectedItems}
                trackBy="id"
                isItemDisabled={(item) => {
                  if (item._errors && item._errors.length > 0) return true;
                  // Require key fields for staging
                  const required = ['cognosChannelType', 'hcpChannelType', 'cognosChannel', 'derivedCognosChannel', 'groupCode', 'hcpMerchantChannel', 'marketplaceId', 'derivedMarketplaceId'];
                  return required.some(f => !item[f] || String(item[f]).trim() === '');
                }}
              />
              <TablePagination pageSize={20} />
            </Table>
          )}
        </SpaceBetween>
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/channel_mapping/cm_bulk_upload.jsx